import { Button, TextField, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLogIn } from "@src/entities/login/model/hooks/useLogIn/useLogIn";
import { Controller, ControllerRenderProps } from "react-hook-form";
import {
  textFieldStyles,
  forgotPasswordButtonStyles,
  submitButtonStyles,
  backButtonStyles,
} from "./theme";
import {
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  FormHelperText,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useAuthStore } from "@src/entities/login/model/store/authStore";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Navigate } from "react-router-dom";
import { reachGoal } from "@src/shared/lib/analytics/yandex/reachGoal";
import { YM_GOALS } from "@src/shared/lib/analytics/yandex/constants";

import styles from "./Autorization.module.scss";

interface AutorizationFormProps {
  email: string;
  password: string;
}

export const AutorizationForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { mutate: logIn, isPending, error } = useLogIn();
  const [showPassword, setShowPassword] = useState(false);
  const { isAuthenticated } = useAuthStore();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
  } = useForm<AutorizationFormProps>({
    defaultValues: {
      email: searchParams.get("login") || "",
      password: "",
    },
    mode: "onTouched",
  });

  useEffect(() => {
    const login = searchParams.get("login");
    if (login) {
      setValue("email", login);
    }
  }, [searchParams, setValue]);

  useEffect(() => {
    setValue("password", "");
  }, [error]);

  useEffect(() => {
    reachGoal(YM_GOALS.AUTORIZATION_FORM_OPEN);
  }, []);

  const onSubmit = (data: AutorizationFormProps) => {
    logIn(
      { login: data.email, password: data.password },
      { onSuccess: () => reachGoal(YM_GOALS.AUTORIZATION_SUBMIT) }
    );
  };

  if (isAuthenticated) {
    return <Navigate to="/" />;
  }

  return (
    <>
      <Typography className={styles.formTitle}>Авторизация</Typography>
      <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        <Controller
          name="email"
          control={control}
          rules={{
            required: "Поле обязательно для заполнения",
            pattern: {
              value: /^[\w-\\.]+@([\w-]+\.)+[\w-]{2,4}$/g,
              message: "Неверный формат email",
            },
          }}
          render={({
            field,
          }: {
            field: ControllerRenderProps<AutorizationFormProps, "email">;
          }) => (
            <TextField
              {...field}
              label="Почта"
              variant="outlined"
              fullWidth
              error={!!errors.email}
              helperText={errors.email?.message}
              className={styles.input}
              InputProps={{
                style: {
                  borderRadius: 24,
                  color: "#fff",
                },
              }}
              InputLabelProps={{
                style: { color: "#ffffff83" },
              }}
              sx={textFieldStyles}
            />
          )}
        />
        <Controller
          name="password"
          control={control}
          rules={{
            required: "Поле обязательно для заполнения",
            minLength: {
              value: 8,
              message: "Пароль должен быть не менее 8 символов",
            },
            validate: {
              hasLetter: (value) =>
                /[A-Za-z]/.test(value) || "Добавьте хотя бы одну букву",
              hasNumber: (value) =>
                /\d/.test(value) || "Добавьте хотя бы одну цифру",
              hasSpecialChar: (value) =>
                /[@$!%*#?&]/.test(value) ||
                "Добавьте хотя бы один специальный символ (@$!%*#?&)",
            },
          }}
          render={({
            field,
          }: {
            field: ControllerRenderProps<AutorizationFormProps, "password">;
          }) => (
            <FormControl
              className={styles.emailInput}
              sx={textFieldStyles}
              variant="outlined"
              fullWidth
            >
              <InputLabel
                htmlFor="outlined-adornment-password"
                sx={{ color: "#ffffff83" }}
              >
                Пароль
              </InputLabel>
              <OutlinedInput
                {...field}
                sx={{
                  borderRadius: 24,
                  color: "#fff",
                }}
                error={!!errors.password}
                type={showPassword ? "text" : "password"}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      sx={{ color: "#fff", mr: 1 }}
                      onClick={() => setShowPassword((show) => !show)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label="Password"
              />
              <FormHelperText error={!!errors.password}>
                {errors.password?.message}
              </FormHelperText>
            </FormControl>
          )}
        />

        <Button
          onClick={() =>
            navigate(`/resetPassword?login=${searchParams.get("login")}`)
          }
          variant="text"
          sx={forgotPasswordButtonStyles}
        >
          Забыли пароль?
        </Button>

        <Button
          disabled={!isValid || isPending}
          type="submit"
          variant="contained"
          className={styles.button}
          sx={submitButtonStyles}
        >
          Продолжить
        </Button>
      </form>
      <Button
        variant="text"
        sx={backButtonStyles}
        onClick={() => navigate("/login")}
      >
        Вернуться
      </Button>
    </>
  );
};
